import {
  DEFERRED_SRC_SELECTOR,
  materializeDeferredMediaSrc,
  releaseMediaSrcIn,
  waitForMediaSettled,
} from '@/helpers/deferredMediaSrc';

// ビューアのメディア読み込み範囲制御。
// 開いたスライド以外は src を保留しておき（createSlide の deferSrc）、
// 表示中のスライドを最優先に読み込んだあと、決めた順番で前の読み込みが落ち着いてから 1 件ずつ読み込む。
// 順番に含まれないものは読まず、範囲から外れたスライドの動画は保留へ戻して取得を打ち切る。
let loadWindowSeq = 0;

// スライド内の保留メディアを読み込み開始する。読み込みを始めたメディアを返す
const materializeSlideMedia = (slide) => {
  const started = [];
  slide?.querySelectorAll?.(DEFERRED_SRC_SELECTOR).forEach((mediaEl) => {
    if (!materializeDeferredMediaSrc(mediaEl)) return;
    if (mediaEl.tagName === 'VIDEO') {
      try { mediaEl.load(); } catch { void 0; }
    }
    started.push(mediaEl);
  });
  return started;
};

const findActiveMedia = (slide) => (
  slide?.querySelector?.('video.storeeel-movie, img.storeeel-media-image') || null
);

const findNeighborSlides = (activeSlide) => {
  const container = activeSlide?.parentElement;
  return {
    nextSlide: container?.querySelector?.(':scope > .swiper-slide-next') || activeSlide?.nextElementSibling || null,
    prevSlide: container?.querySelector?.(':scope > .swiper-slide-prev') || activeSlide?.previousElementSibling || null,
  };
};

// スワイプ中に覗く隣のスライドは、順番を待たずにすぐ読み込む（iPhone の隣接プレビュー再生用）
export const ensureReelSlideMediaLoaded = (slide) => {
  materializeSlideMedia(slide);
};

// keepSlides 以外のスライドの動画を解放する。
// 通り過ぎた動画を pause だけで残すと通信が開いたまま溜まり、数本で後続の動画が届かなくなる。
// loop でも位置は Swiper が付けるクラス（物理 DOM）で判定し、同じ index の別スライドは残さない。
const releaseSlidesOutside = (activeSlide, keepSlides) => {
  const container = activeSlide?.parentElement;
  if (!container) return;
  const keep = new Set([activeSlide, ...keepSlides].filter(Boolean));
  container.querySelectorAll(':scope > .swiper-slide').forEach((slide) => {
    if (keep.has(slide)) return;
    releaseMediaSrcIn(slide);
  });
};

let releaseTimerId = null;

// 表示中のスライドを即読み込み、落ち着いたら steps を順に処理する。
// step は「読み込みを始めたメディアの配列」を返す関数。コンテンツが切り替わったら続きは打ち切る。
// keepSlides を渡すと、表示中と keepSlides 以外のスライドの動画を解放する。
// releaseDelayMs を渡すと、解放をその時間だけ遅らせる（遷移の開始時に呼ばれる PC 用）。
// 解放した動画は寸法と映像を失って縮み黒くなるため、画面上を流れて出ていく途中で解放しないようにする。
// 待つ間に次の呼び出しがあれば、この回の解放はやめて新しい回に任せる（新しい範囲で解放される）。
export const loadMediaWindow = (activeSlide, steps = [], { keepSlides = null, releaseDelayMs = 0 } = {}) => {
  if (!activeSlide) return;
  loadWindowSeq += 1;
  const seq = loadWindowSeq;
  if (releaseTimerId != null) {
    clearTimeout(releaseTimerId);
    releaseTimerId = null;
  }
  if (keepSlides && releaseDelayMs > 0) {
    releaseTimerId = setTimeout(() => {
      releaseTimerId = null;
      if (seq === loadWindowSeq) releaseSlidesOutside(activeSlide, keepSlides);
    }, releaseDelayMs);
  } else if (keepSlides) {
    // 古い順番待ちが解放済みスライドを読み直さないよう、seq を進めてから同じ同期処理内で解放する
    releaseSlidesOutside(activeSlide, keepSlides);
  }
  materializeSlideMedia(activeSlide);

  (async () => {
    await waitForMediaSettled(findActiveMedia(activeSlide));
    for (const step of steps) {
      if (seq !== loadWindowSeq) return;
      const started = step() || [];
      for (const mediaEl of started) {
        await waitForMediaSettled(mediaEl);
        if (seq !== loadWindowSeq) return;
      }
    }
  })().catch(() => { void 0; });
};

// スライドを読み込む step を作る
export const slideStep = (slide) => () => materializeSlideMedia(slide);

// SP リール型・タイル型: 表示中 → 次 → 前（それ以外は解放）
export const loadReelMediaWindow = (activeSlide) => {
  const { nextSlide, prevSlide } = findNeighborSlides(activeSlide);
  loadMediaWindow(activeSlide, [slideStep(nextSlide), slideStep(prevSlide)], {
    keepSlides: [nextSlide, prevSlide],
  });
};

export { findNeighborSlides };
